'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useGetGifts, useGiftEntityRows } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { Skeleton } from '@/components/ui/app-skeleton';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { useAppRouter } from '@/lib/network/use-app-router';
import { GIFT_LIST_FIELDS } from '@/lib/sdk/projections';
import type { GiftRowData, GiftStatusFilter } from './gift-row';
import { GiftRow } from './gift-row';
import type { GiftAmountInput } from './gifts-total-amount';
import { GiftsTotalAmount } from './gifts-total-amount';
// VaultClaimGroup deleted — the new contract is 1·1 (no grouping).

export interface GiftsReceivedListProps {
  status?: GiftStatusFilter;
}
interface VaultGroup {
  /**
   * Stable group key — `vaultPubkey` when present, otherwise the
   * recipientHandle as a defensive fallback (the SDK does not surface
   * vaultPubkey yet — see `GiftRowData.vaultPubkey`).
   */
  key: string;
  recipientHandle: string;
  gifts: GiftRowData[];
}

/**
 * Group pending received gifts by HandleVault. Defensive fallback: when
 * `vaultPubkey` is absent (the SDK shape will gain it in C3 but doesn't
 * have it today), we group by `recipientHandle` since every deposit to the
 * same handle drains the same vault on-chain anyway.
 */
function groupPendingByVault(rows: ReadonlyArray<GiftRowData>): VaultGroup[] {
  const groups: Map<string, VaultGroup> = new Map();
  for (const r of rows) {
    const key = r.vaultPubkey !== undefined && r.vaultPubkey.length > 0 ? r.vaultPubkey : `handle:${r.recipientHandle}`;
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, {
        key,
        recipientHandle: r.recipientHandle,
        gifts: [r]
      });
    } else {
      groups.set(key, {
        ...existing,
        gifts: [...existing.gifts, r]
      });
    }
  }
  return Array.from(groups.values());
}
function toAmountInputs(rows: ReadonlyArray<GiftRowData>): GiftAmountInput[] {
  return rows.map((r): GiftAmountInput => ({
    key: r.opaqueId,
    assetKind: r.assetKind,
    amount: r.amount,
    mintPubkey: r.mintPubkey ?? null
  }));
}

/**
 * Recipient inbox (US-7 + spec 006-handle-gifting). Empty/loading/error
 * states all render via i18n keys. When the active status filter shows
 * pending gifts, deposits are collapsed into per-vault groups with a single
 * "Claim all" CTA per group; non-pending gifts continue to render as
 * individual rows because per-deposit history matters there.
 *
 * When called without a `status` prop (the default for the Received tab),
 * the list renders two labelled sections: "to claim" (pending) and
 * "claimed" (history), each with a count + USDC total summary.
 */
export function GiftsReceivedList(props) {
  const $ = _c(47);
  const t = useTranslations("gift.inbox");
  useAppRouter();
  const network = useActiveNetwork();
  const {
    user
  } = useAuth();
  const userId = user?.id ?? "";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [...GIFT_LIST_FIELDS];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.status) {
    t1 = props.status !== undefined ? {
      status: props.status
    } : {};
    $[1] = props.status;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1 || $[4] !== userId) {
    t2 = {
      userId,
      kind: "received",
      fields: t0,
      ...t1
    };
    $[3] = t1;
    $[4] = userId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = userId !== "";
  let t4;
  if ($[6] !== t3) {
    t4 = {
      enabled: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const query = useGetGifts(t2, t4);
  const liveData = useLiveListData(query.queryKey);
  const canonicalRows = useGiftEntityRows(liveData, network);
  const data = query.isPending ? undefined : canonicalRows;
  const isLoading = userId === "" || query.isPending;
  const isError = !isLoading && query.isError;
  if (isLoading) {
    let t5;
    if ($[8] !== t) {
      t5 = t("loading");
      $[8] = t;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Skeleton className="h-24 w-full" />;
      t7 = <Skeleton className="h-24 w-full" />;
      $[10] = t6;
      $[11] = t7;
    } else {
      t6 = $[10];
      t7 = $[11];
    }
    let t8;
    if ($[12] !== t5) {
      t8 = <output className="flex flex-col gap-3" aria-label={t5}>{t6}{t7}</output>;
      $[12] = t5;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  if (isError || data === undefined) {
    let t5;
    if ($[14] !== t) {
      t5 = t("empty.received");
      $[14] = t;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== t5) {
      t6 = <div className="rounded-control border border-feedback-error-border/30 bg-feedback-error-accent/5 p-4 text-body text-feedback-error-content">{t5}</div>;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  if (data.length === 0) {
    let t5;
    if ($[18] !== t) {
      t5 = t("empty.received");
      $[18] = t;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== t5) {
      t6 = <div className="rounded-control border border-dashed border-border-primary p-6 text-center text-body text-foreground-secondary">{t5}</div>;
      $[20] = t5;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    return t6;
  }
  let t10;
  let t11;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[22] !== data || $[23] !== props.status || $[24] !== t) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const pending = data.filter(_temp);
      const claimed = data.filter(_temp2);
      const settled = data.filter(_temp3);
      if (props.status !== undefined) {
        t11 = <div className="flex flex-col gap-3">{pending.map(_temp4)}{settled.map(_temp5)}</div>;
        break bb0;
      }
      t9 = "flex flex-col gap-6";
      const t12 = <header className="flex items-baseline justify-between gap-3"><h3 id="gifts-received-pending-heading" className="text-heading-xs">{t("sections.pending", {
            count: pending.length
          })}</h3>{pending.length > 0 ? <span className="inline-flex items-baseline gap-1 text-heading-lg text-foreground-primary"><span>{t("sections.total")}</span><GiftsTotalAmount rows={toAmountInputs(pending)} testId="gifts-pending-total" /></span> : null}</header>;
      const t13 = pending.length === 0 ? <div className="rounded-control border border-dashed border-border-primary p-4 text-center text-caption text-foreground-secondary">{t("empty.no_pending_received")}</div> : pending.map(_temp6);
      if ($[32] !== t12 || $[33] !== t13) {
        t10 = <section className="flex flex-col gap-3" aria-labelledby="gifts-received-pending-heading">{t12}{t13}</section>;
        $[32] = t12;
        $[33] = t13;
        $[34] = t10;
      } else {
        t10 = $[34];
      }
      t5 = "flex flex-col gap-3";
      t6 = "gifts-received-claimed-heading";
      const t14 = <h3 id="gifts-received-claimed-heading" className="text-heading-xs">{t("sections.claimed", {
          count: claimed.length
        })}</h3>;
      const t15 = claimed.length > 0 ? <span className="inline-flex items-baseline gap-1 text-heading-lg text-foreground-primary"><span>{t("sections.total")}</span><GiftsTotalAmount rows={toAmountInputs(claimed)} testId="gifts-claimed-total" /></span> : null;
      if ($[35] !== t14 || $[36] !== t15) {
        t7 = <header className="flex items-baseline justify-between gap-3">{t14}{t15}</header>;
        $[35] = t14;
        $[36] = t15;
        $[37] = t7;
      } else {
        t7 = $[37];
      }
      t8 = settled.length === 0 ? <div className="rounded-control border border-dashed border-border-primary p-4 text-center text-caption text-foreground-secondary">{t("empty.no_claimed_received")}</div> : settled.map(_temp7);
    }
    $[22] = data;
    $[23] = props.status;
    $[24] = t;
    $[25] = t10;
    $[26] = t11;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t10 = $[25];
    t11 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[38] !== t5 || $[39] !== t6 || $[40] !== t7 || $[41] !== t8) {
    t12 = <section className={t5} aria-labelledby={t6}>{t7}{t8}</section>;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t10 || $[44] !== t12 || $[45] !== t9) {
    t13 = <div className={t9}>{t10}{t12}</div>;
    $[43] = t10;
    $[44] = t12;
    $[45] = t9;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  return t13;
}
function _temp7(row_2) {
  return <GiftRow key={row_2.opaqueId} variant="received" data={row_2} />;
}
function _temp6(row_1) {
  return <GiftRow key={row_1.opaqueId} variant="received" data={row_1} />;
}
function _temp5(row_0) {
  return <GiftRow key={row_0.opaqueId} variant="received" data={row_0} />;
}
function _temp4(row) {
  return <GiftRow key={row.opaqueId} variant="received" data={row} />;
}
function _temp3(r_1) {
  return r_1.status !== "pending";
}
function _temp2(r_0) {
  return r_0.status === "claimed";
}
function _temp(r) {
  return r.status === "pending";
}